import React from 'react'
import * as R from 'remeda'

import {
  ItemMultiSelector,
  type ItemMultiSelectorProps,
} from '~/components/elements/ItemMultiSelector'
import { type SurveyGroupAnswerOption as SurveyGroupAnswerOptionBase } from '~/gql/graphql'
import { SurveyGroupAnswerOptionOp } from '~/utils'

type SurveyGroupAnswerOption = App.PickGraphQL<
  SurveyGroupAnswerOptionBase,
  'option' | 'text'
>

export type SurveyGroupAnswerOptionMultiSelectorProps<
  TSurveyGroupAnswerOption extends SurveyGroupAnswerOption,
> = Omit<
  ItemMultiSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  answerOptions: TSurveyGroupAnswerOption[]
  onChange?: (answerOptions: TSurveyGroupAnswerOption['option'][]) => void
  value: TSurveyGroupAnswerOption['option'][]
}

export const SurveyGroupAnswerOptionMultiSelector = React.forwardRef(
  <TSurveyGroupAnswerOption extends SurveyGroupAnswerOption>(
    {
      answerOptions,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupAnswerOptionMultiSelectorProps<TSurveyGroupAnswerOption>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    type DataItem = TSurveyGroupAnswerOption & { textField: string }
    const data = React.useMemo<DataItem[]>(
      () =>
        answerOptions.map((answerOption) => ({
          ...answerOption,
          textField: SurveyGroupAnswerOptionOp.toText(answerOption),
        })),
      [answerOptions],
    )
    const value = React.useMemo<DataItem[]>(
      () => data.filter(({ option }) => valueProp.includes(option)),
      [data, valueProp],
    )

    const handleChange = React.useCallback<
      EH<ItemMultiSelectorProps, 'onChange'>
    >(
      (value: DataItem[]) => {
        onChange?.(value.map(R.prop('option')))
      },
      [onChange],
    )

    return (
      <ItemMultiSelector
        ref={ref}
        data={data}
        dataItemKey="option"
        textField="textField"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
